import React, { useMemo, useState, useEffect } from "react";
import useTemplatesHook from "../services/twilio/useTemplatesHook";
import { useBusinessConfig } from "../context/BusinessConfigContext";
import { SendTemplateModal } from "./modals/SendTemplateModal";
import { adminAPI } from '../services/api/adminAPI';
import { WhatsAppMessagePreview } from './WhatsAppMessagePreview';

function transliterateHebrew(str) {
  const map = {
    'א':'a','ב':'b','ג':'g','ד':'d','ה':'h','ו':'v','ז':'z','ח':'kh','ט':'t',
    'י':'y','ך':'k','כ':'k','ל':'l','ם':'m','מ':'m','ן':'n','נ':'n','ס':'s',
    'ע':'a','ף':'f','פ':'f','ץ':'ts','צ':'ts','ק':'k','ר':'r','ש':'sh','ת':'t',
    // niqqud and marks will be stripped by normalize step
  };
  let out = '';
  for (const ch of str) {
    if (map[ch]) out += map[ch];
    else out += ch;
  }
  return out;
}

function toLegalTemplateName(input) {
  if (!input) return "";
  // 1) Transliterate Hebrew to simple Latin approximations
  const basic = transliterateHebrew(input);
  // 2) Strip diacritics and fold to ASCII
  const ascii = basic
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "");
  // 3) Keep only lowercase letters, numbers, underscore
  let out = ascii
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
  // 4) Must start with a letter; if not, prefix
  if (!/^[a-z]/.test(out)) {
    out = `tmpl_${out}`;
  }
  if (!out) out = `tmpl_${Date.now()}`;
  return out.slice(0, 128);
}

function replaceMetaVariables(text, variablesMapping = {}) {
  let result = text;
  Object.entries(variablesMapping).forEach(([varName, twilioVar]) => {
    result = result.replaceAll(varName, twilioVar);
  });
  return result;
}

function StatusBadge({ status }) {
  const map = {
    approved: "bg-green-100 text-green-800",
    pending: "bg-yellow-100 text-yellow-800",
    declined: "bg-red-100 text-red-800",
  };
  const cls = map[status] || "bg-gray-100 text-gray-800";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

export default function TemplateManager() {
  const { templates, createTemplate, syncApprovalStatuses } = useTemplatesHook();
  const { config } = useBusinessConfig();

  // Modal state - must be declared before other hooks
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form state
  const [displayName, setDisplayName] = useState("");
  const [headerType, setHeaderType] = useState("text"); // text, image, video, none
  const [headerText, setHeaderText] = useState(config.business_name);
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const machineName = useMemo(() => toLegalTemplateName(displayName), [displayName]);
  const [type] = useState("text/cta"); // only one option for now
  const [text, setText] = useState("");
  const [templateCategory, setTemplateCategory] = useState("MARKETING");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Character limits
  const LIMITS = {
    HEADER_TEXT: 60,
    BODY: 1024,
    BUTTON_TITLE: 25
  };

  // Multiple buttons state
  const [buttons, setButtons] = useState([]);

  // Variables state - track custom variables defined in template
  const [variables, setVariables] = useState([{ id: 1, name: 'שם לקוח', placeholder: 'שם הלקוח' }]); // Default customer name variable

  // Button limits per WhatsApp rules: up to 2 URL + 10 quick reply
  // PHONE button temporarily disabled for testing
  const canAddButton = useMemo(() => {
    const urlCount = buttons.filter(b => b.type === 'URL').length;
    const quickReplyCount = buttons.filter(b => b.type === 'QUICK_REPLY').length;
    return urlCount < 2 || quickReplyCount < 10;
  }, [buttons]);

  const getAvailableButtonTypes = useMemo(() => {
    const urlCount = buttons.filter(b => b.type === 'URL').length;
    const quickReplyCount = buttons.filter(b => b.type === 'QUICK_REPLY').length;

    const types = [];
    if (urlCount < 2) types.push({ value: 'URL', label: 'כפתור URL' });
    if (quickReplyCount < 10) types.push({ value: 'QUICK_REPLY', label: 'תשובה מהירה' });
    // PHONE button temporarily disabled for testing
    // if (phoneCount < 1) types.push({ value: 'PHONE', label: 'כפתור טלפון' });
    return types;
  }, [buttons]);

  // Sync approval statuses on component mount
  useEffect(() => {
    if (templates.length > 0) {
      syncApprovalStatuses();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInsertVar = (varName) => {
    setText((t) => (t ? `${t} {${varName}}` : `{${varName}}`));
  };

  const handleAddVariable = () => {
    const newId = Math.max(...variables.map(v => v.id), 0) + 1;
    setVariables([...variables, { id: newId, name: `משתנה ${newId}`, placeholder: '' }]);
  };

  const handleRemoveVariable = (id) => {
    // Don't allow removing the first customer name variable
    if (id === 1) return;
    const varToRemove = variables.find(v => v.id === id);
    if (varToRemove) {
      // Remove variable from text
      setText(text.replace(new RegExp(`\\{${varToRemove.name}\\}`, 'g'), ''));
      setVariables(variables.filter(v => v.id !== id));
    }
  };

  const handleVariableChange = (id, field, value) => {
    setVariables(variables.map(v => {
      if (v.id === id) {
        const oldName = v.name;
        const newVar = { ...v, [field]: value };

        // If name changed, update text
        if (field === 'name' && oldName !== value) {
          setText(text.replace(new RegExp(`\\{${oldName}\\}`, 'g'), `{${value}}`));
        }

        return newVar;
      }
      return v;
    }));
  };

  const handleAddButton = () => {
    const availableTypes = getAvailableButtonTypes;
    if (availableTypes.length === 0) return;
    setButtons([...buttons, {
      id: Date.now(),
      type: availableTypes[0].value,
      title: '',
      value: ''
    }]);
  };

  const handleRemoveButton = (id) => {
    setButtons(buttons.filter(b => b.id !== id));
  };

  const handleButtonChange = (id, field, value) => {
    // Auto-format phone numbers for Israeli numbers
    if (field === 'value') {
      const button = buttons.find(b => b.id === id);
      if (button?.type === 'PHONE' && value) {
        let cleanNumber = value.replace(/[\s\-()]/g, '');
        if (!cleanNumber.startsWith('+')) {
          if (cleanNumber.startsWith('0')) {
            cleanNumber = '+972' + cleanNumber.substring(1);
          }
        }
        value = cleanNumber;
      }
    }
    setButtons(buttons.map(b => b.id === id ? { ...b, [field]: value } : b));
  };

  const handleRefreshStatuses = async () => {
    setIsRefreshing(true);
    try {
      await syncApprovalStatuses();
    } catch (err) {
      console.error("Failed to refresh approval statuses", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSendTemplate = (template) => {
    setSelectedTemplate(template);
    setIsSendModalOpen(true);
  };

  const handleSendTemplateSubmit = async ({ phoneNumber, contentSid, variables }) => {
    try {
      await adminAPI.sendWhatsAppTemplate({
        phoneNumber,
        contentSid,
        variables
      });
    } catch (err) {
      console.error("Failed to send template", err);
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!displayName.trim()) {
      setError("יש להזין שם לתבנית");
      return;
    }
    if (!text.trim()) {
      setError("יש להזין טקסט לתבנית");
      return;
    }

    // Validate header
    if (headerType === 'text' && headerText.length > LIMITS.HEADER_TEXT) {
      setError(`כותרת טקסט יכולה להכיל עד ${LIMITS.HEADER_TEXT} תווים`);
      return;
    }

    if (headerType === 'image' && !headerMediaUrl.trim()) {
      setError("יש להזין URL לתמונה");
      return;
    }

    if (headerType === 'image' && headerMediaUrl.trim()) {
      try {
        new URL(headerMediaUrl);
      } catch {
        setError("URL תמונה לא תקין");
        return;
      }
      if (!headerMediaUrl.startsWith('https://')) {
        setError("URL תמונה חייב להתחיל ב-https://");
        return;
      }
    }

    if (headerType === 'video' && !headerMediaUrl.trim()) {
      setError("יש להזין URL לסרטון");
      return;
    }

    if (headerType === 'video' && headerMediaUrl.trim()) {
      try {
        new URL(headerMediaUrl);
      } catch {
        setError("URL סרטון לא תקין");
        return;
      }
      if (!headerMediaUrl.startsWith('https://')) {
        setError("URL סרטון חייב להתחיל ב-https://");
        return;
      }
    }

    if (text.length > LIMITS.BODY) {
      setError(`טקסט ההודעה יכול להכיל עד ${LIMITS.BODY} תווים`);
      return;
    }

    // Validate buttons
    for (const button of buttons) {
      if (!button.title.trim()) {
        setError("כל הכפתורים חייבים להכיל טקסט");
        return;
      }
      if (button.type === 'URL' && !button.value.trim()) {
        setError("כפתור URL חייב להכיל כתובת");
        return;
      }
      if (button.type === 'PHONE') {
        if (!button.value.trim()) {
          setError("כפתור טלפון חייב להכיל מספר טלפון");
          return;
        }
        if (!button.value.startsWith('+')) {
          setError("מספר טלפון חייב להיות בפורמט בינלאומי (להתחיל ב-+)");
          return;
        }
      }
    }

    // Build variables mapping - convert {varName} to {{1}}, {{2}}, etc.
    const variablesMapping = {};
    const variablesList = [];
    variables.forEach((v, index) => {
      variablesMapping[`{${v.name}}`] = `{{${index + 1}}}`;
      variablesList.push({
        position: index + 1,
        name: v.name,
        placeholder: v.placeholder || v.name
      });
    });

    const payload = {
      displayName: displayName.trim(),
      headerType,
      headerText: headerType === 'text' ? headerText : undefined,
      headerMediaUrl: (headerType === 'image' || headerType === 'video') ? headerMediaUrl : undefined,
      machineName,
      type,
      textRaw: text,
      textWithVariables: replaceMetaVariables(text, variablesMapping),
      variablesMapping,
      variables: variablesList,
      buttons: buttons.length > 0 ? buttons.map(b => ({
        type: b.type,
        title: b.title,
        value: b.value
      })) : [],
      category: templateCategory,
    };

    try {
      setSubmitting(true);
      await createTemplate(payload);
      // Reset form
      setDisplayName("");
      setHeaderType("text");
      setHeaderText(config.business_name);
      setHeaderMediaUrl("");
      setText("");
      setButtons([]);
      setVariables([{ id: 1, name: 'שם לקוח', placeholder: 'שם הלקוח' }]);
      setTemplateCategory("MARKETING");
      // Close modal on success
      setIsModalOpen(false);
    } catch (err) {
      console.error("Failed to create template", err);
      setError("אירעה שגיאה בשליחת התבנית");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-6" dir="rtl">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex flex-col items-center mb-6">
          <h1 className="text-2xl font-semibold mb-4">ניהול תבניות לקמפיינים</h1>

          {templates.length > 0 && (
            <div className="flex gap-3">
              <button
                onClick={() => setIsModalOpen(true)}
                className="rounded-xl bg-indigo-600 text-white px-6 py-2.5 hover:bg-indigo-700 transition-colors shadow-sm"
              >
                הוסף תבנית חדשה
              </button>
              <button
                onClick={handleRefreshStatuses}
                disabled={isRefreshing}
                className="rounded-xl bg-gray-600 text-white px-6 py-2.5 hover:bg-gray-700 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg
                  className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {isRefreshing ? 'מרענן...' : 'רענן סטטוס אישורים'}
              </button>
            </div>
          )}
        </div>

        <section>
          {templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="text-center mb-6">
                <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">אין תבניות עדיין</h2>
                <p className="text-gray-600 max-w-md">
                  צרו את התבנית הראשונה שלכם כדי להתחיל לשלוח קמפיינים מותאמים אישית ללקוחות שלכם
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="rounded-xl bg-indigo-600 text-white px-6 py-2.5 hover:bg-indigo-700 transition-colors shadow-sm"
              >
                הוסף תבנית חדשה
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold mb-4">התבניות שלי</h2>
              <div className="space-y-3">
                {templates.map((t) => (
                  <article key={t.id} className="rounded-2xl border border-gray-200 p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-medium">{t.displayName}</h3>
                        <span className="text-xs font-mono text-gray-500">{t.machineName}</span>
                      </div>
                      <StatusBadge status={t.status} />
                    </div>
                    <h3 className="text-md font-medium">{t.headerText}</h3>
                    <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{t.textRaw}</div>
                    {t.buttons && t.buttons.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {t.buttons.map((btn, idx) => (
                          <span key={idx} className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
                            {btn.type === 'URL' && '🔗'}
                            {btn.type === 'PHONE' && '📞'}
                            {btn.type === 'QUICK_REPLY' && '💬'}
                            {' '}{btn.title}
                          </span>
                        ))}
                      </div>
                    )}
                    {t.cta && !t.buttons && (
                      <div className="mt-2">
                        <span className="text-xs text-gray-500">CTA: </span>
                        <span className="text-sm">{t.cta.label}</span>
                      </div>
                    )}
                    {t.variables && t.variables.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-gray-500">משתנים: </span>
                        <span className="text-xs text-gray-700">
                          {t.variables.map(v => v.name).join(', ')}
                        </span>
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        נשלח ב־{new Date(t.createdAt).toLocaleString("he-IL")}
                      </div>
                      {t.status === 'approved' && (
                        <button
                          onClick={() => handleSendTemplate(t)}
                          className="rounded-lg bg-green-600 text-white px-3 py-1.5 text-sm hover:bg-green-700"
                        >
                          שלח הודעה
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setIsModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex" onClick={(e) => e.stopPropagation()}>
            {/* Form Section */}
            <div className="flex-1 overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-semibold">הוסף תבנית חדשה</h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">שם תבנית (חופשי)</label>
                <input
                  type="text"
                  className="w-full rounded-xl border px-3 py-2"
                  placeholder="למשל: ברוכים הבאים ללקוחות חדשים"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  שם טכני שייווצר: <span className="font-mono">{machineName || "—"}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">סוג כותרת</label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setHeaderType('text')}
                    className={`flex-1 min-w-[100px] rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                      headerType === 'text'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    טקסט
                  </button>
                  <button
                    type="button"
                    onClick={() => setHeaderType('image')}
                    className={`flex-1 min-w-[100px] rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                      headerType === 'image'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    תמונה
                  </button>
                  <button
                    type="button"
                    onClick={() => setHeaderType('video')}
                    className={`flex-1 min-w-[100px] rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                      headerType === 'video'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    סרטון
                  </button>
                  <button
                    type="button"
                    onClick={() => setHeaderType('none')}
                    className={`flex-1 min-w-[100px] rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                      headerType === 'none'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ללא כותרת
                  </button>
                </div>
              </div>

              {headerType === 'text' && (
                <div>
                  <label className="block text-sm font-medium mb-1">כותרת ההודעה</label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full rounded-xl border px-3 py-2"
                      placeholder={config.business_name}
                      value={headerText}
                      onChange={(e) => setHeaderText(e.target.value)}
                      maxLength={LIMITS.HEADER_TEXT}
                    />
                    <div className="mt-1 flex items-center justify-between">
                      <p className="text-xs text-orange-600">
                        מומלץ מאוד לכלול את שם העסק בכותרת או בתוכן ההודעה
                      </p>
                      <p className={`text-xs ${headerText.length >= LIMITS.HEADER_TEXT ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                        {headerText.length}/{LIMITS.HEADER_TEXT}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {(headerType === 'image' || headerType === 'video') && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {headerType === 'image' ? 'URL תמונה' : 'URL סרטון'}
                  </label>
                  <input
                    type="url"
                    className="w-full rounded-xl border px-3 py-2"
                    placeholder={`https://example.com/${headerType === 'image' ? 'image.jpg' : 'video.mp4'}`}
                    value={headerMediaUrl}
                    onChange={(e) => setHeaderMediaUrl(e.target.value)}
                    dir="ltr"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    יש להזין קישור ציבורי ל{headerType === 'image' ? 'תמונה' : 'סרטון'} (חייב להתחיל ב-https://)
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">קטגוריית תבנית</label>
                <select
                  className="w-full rounded-xl border px-3 py-2"
                  value={templateCategory}
                  onChange={(e) => setTemplateCategory(e.target.value)}
                >
                  <option value="MARKETING">שיווקי (MARKETING)</option>
                  <option value="UTILITY">שירותי (UTILITY)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  הודעות שיווקיות - קידום מכירות ופרסום. הודעות שירותיות - תזכורות, עדכונים ומידע
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">טקסט</label>
                <textarea
                  className="w-full min-h-[140px] rounded-xl border px-3 py-2"
                  placeholder="כתוב/י כאן את תוכן ההודעה..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={LIMITS.BODY}
                />
                <div className={`mt-1 text-xs ${text.length >= LIMITS.BODY ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                  {text.length}/{LIMITS.BODY}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">משתנים</label>
                  <button
                    type="button"
                    onClick={handleAddVariable}
                    className="rounded-lg border border-indigo-600 text-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-50"
                  >
                    + הוסף משתנה
                  </button>
                </div>

                <p className="text-xs text-gray-500">
                  הגדר משתנים שיאכלסו בעת שליחת ההודעה. לחץ על משתנה כדי להוסיף אותו לטקסט.
                </p>

                {variables.map((variable, index) => (
                  <div key={variable.id} className="border rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500">משתנה {index + 1}</span>
                      {variable.id !== 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveVariable(variable.id)}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          הסר
                        </button>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1">שם המשתנה</label>
                      <input
                        type="text"
                        className="w-full rounded-lg border px-2 py-1.5 text-sm"
                        placeholder="למשל: שם לקוח, תאריך תור"
                        value={variable.name}
                        onChange={(e) => handleVariableChange(variable.id, 'name', e.target.value)}
                        disabled={variable.id === 1}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1">הסבר (יוצג בעת שליחה)</label>
                      <input
                        type="text"
                        className="w-full rounded-lg border px-2 py-1.5 text-sm"
                        placeholder="למשל: שם הלקוח המלא"
                        value={variable.placeholder}
                        onChange={(e) => handleVariableChange(variable.id, 'placeholder', e.target.value)}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => handleInsertVar(variable.name)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
                    >
                      הוסף {`{${variable.name}}`} לטקסט
                    </button>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">כפתורים</label>
                  {canAddButton && (
                    <button
                      type="button"
                      onClick={handleAddButton}
                      className="rounded-lg border border-indigo-600 text-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-50"
                    >
                      + הוסף כפתור
                    </button>
                  )}
                </div>

                {buttons.length === 0 && (
                  <p className="text-xs text-gray-500">
                    ניתן להוסיף עד 2 כפתורי URL, עד 10 כפתורי תשובה מהירה, וכפתור טלפון אחד
                  </p>
                )}

                {buttons.map((button) => (
                  <div key={button.id} className="border rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <select
                        className="flex-1 rounded-lg border px-2 py-1 text-sm"
                        value={button.type}
                        onChange={(e) => handleButtonChange(button.id, 'type', e.target.value)}
                      >
                        <option value="URL">כפתור URL</option>
                        <option value="QUICK_REPLY">תשובה מהירה</option>
                        {/* PHONE button temporarily disabled for testing */}
                        {/* <option value="PHONE">כפתור טלפון</option> */}
                      </select>
                      <button
                        type="button"
                        onClick={() => handleRemoveButton(button.id)}
                        className="text-red-600 hover:text-red-700 text-sm mr-2"
                      >
                        הסר
                      </button>
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1">טקסט הכפתור</label>
                      <input
                        type="text"
                        className="w-full rounded-lg border px-2 py-1.5 text-sm"
                        placeholder={button.type === 'PHONE' ? 'התקשר אלינו' : button.type === 'QUICK_REPLY' ? 'כן, מעניין' : 'לחץ כאן'}
                        value={button.title}
                        onChange={(e) => handleButtonChange(button.id, 'title', e.target.value)}
                        maxLength={25}
                      />
                    </div>

                    {button.type === 'URL' && (
                      <div>
                        <label className="block text-xs font-medium mb-1">כתובת URL</label>
                        <input
                          type="url"
                          className="w-full rounded-lg border px-2 py-1.5 text-sm"
                          placeholder="https://example.com"
                          value={button.value}
                          onChange={(e) => handleButtonChange(button.id, 'value', e.target.value)}
                          dir="ltr"
                        />
                      </div>
                    )}

                    {button.type === 'PHONE' && (
                      <div>
                        <label className="block text-xs font-medium mb-1">מספר טלפון</label>
                        <input
                          type="tel"
                          className="w-full rounded-lg border px-2 py-1.5 text-sm"
                          placeholder="050-1234567"
                          value={button.value}
                          onChange={(e) => handleButtonChange(button.id, 'value', e.target.value)}
                          dir="ltr"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          ניתן להזין מספר ישראלי - יומר אוטומטית לפורמט בינלאומי
                        </p>
                      </div>
                    )}

                    {button.type === 'QUICK_REPLY' && (
                      <div>
                        <label className="block text-xs font-medium mb-1">מזהה תשובה (אופציונלי)</label>
                        <input
                          type="text"
                          className="w-full rounded-lg border px-2 py-1.5 text-sm"
                          placeholder="למשל: yes, interested"
                          value={button.value || ''}
                          onChange={(e) => handleButtonChange(button.id, 'value', e.target.value)}
                          dir="ltr"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {error && (
                <div className="text-red-600 text-sm">{error}</div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 rounded-xl bg-indigo-600 text-white px-4 py-2 hover:bg-indigo-700 disabled:opacity-60"
                >
                  {submitting ? "שולח..." : "שלח תבנית"}
                </button>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="rounded-xl border border-gray-300 px-4 py-2 hover:bg-gray-50"
                >
                  ביטול
                </button>
              </div>
            </form>
            </div>

            {/* Preview Section */}
            <div className="w-96 border-r border-gray-200 flex flex-col">
              <div className="bg-[#008069] px-4 py-3 text-white">
                <h3 className="font-semibold text-sm">תצוגה מקדימה</h3>
                <p className="text-xs opacity-90">כך ההודעה תראה ב-WhatsApp</p>
              </div>

              <div className="flex-1 overflow-y-auto">
                <WhatsAppMessagePreview
                  headerType={headerType}
                  headerText={headerText}
                  headerMediaUrl={headerMediaUrl}
                  text={text}
                  buttons={buttons}
                  showContainer={true}
                  alignment="right"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Template Modal */}
      <SendTemplateModal
        isOpen={isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
        template={selectedTemplate}
        onSend={handleSendTemplateSubmit}
      />
    </div>
  );
}
